import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ScrollReveal from "@/components/ScrollReveal";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { getSectionBySlug, sections } from "@/data/leistungenSections";

const LeistungDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const section = slug ? getSectionBySlug(slug) : undefined;

  if (!section) return <Navigate to="/leistungen" replace />;

  const others = sections.filter((s) => s.slug !== section.slug);
  const Icon = section.icon;

  return (
    <div className="min-h-screen bg-background">
      <SEO title={section.seoTitle} description={section.seoDescription} path={`/leistungen/${section.slug}`} />
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-20 bg-gradient-hero">
        <div className="container mx-auto px-4">
          <ScrollReveal>
            <Link to="/leistungen" className="text-primary/80 hover:text-primary text-sm tracking-wider uppercase">
              ← Alle Leistungen
            </Link>
            <div className="flex items-center gap-4 mt-6 mb-4">
              <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center">
                <Icon size={28} className="text-primary" />
              </div>
              <span className="text-primary text-sm font-medium tracking-wider uppercase">Leistung</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold text-foreground mb-6">{section.title}</h1>
            <p className="text-xl text-foreground/85 max-w-2xl">{section.longDescription}</p>
          </ScrollReveal>
        </div>
      </section>

      {/* Items */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <ScrollReveal>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-12">Was wir liefern</h2>
          </ScrollReveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {section.items.map((item, i) => (
              <ScrollReveal key={item.title} delay={i * 100}>
                <div className="p-8 rounded-2xl bg-gradient-card border border-border/50 hover:border-primary/20 transition-all duration-300 h-full">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-5">
                    <item.icon size={22} className="text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">{item.title}</h3>
                  <p className="text-foreground/85 text-base leading-relaxed">{item.text}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Packages */}
      <section className="py-24 bg-card/30">
        <div className="container mx-auto px-4">
          <ScrollReveal>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-12">Unser Service</h2>
          </ScrollReveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {section.packages.map((pkg, pIdx) => (
              <ScrollReveal key={pkg.name} delay={pIdx * 100}>
                <div className="p-6 rounded-2xl border border-border/50 bg-gradient-card hover:border-primary/20 transition-all duration-300 flex flex-col h-full">
                  <div className="flex flex-col items-start gap-1 mb-3">
                    <span className="text-primary font-bold text-base">{String(pIdx + 1).padStart(2, "0")}</span>
                    <span className="font-semibold text-xl text-foreground" style={{ fontFamily: '"agency-fb", sans-serif', fontWeight: 900, fontStyle: "italic", textTransform: "uppercase" }}>{pkg.name}</span>
                  </div>
                  <p className="text-foreground/85 text-base mb-5 whitespace-pre-line">{pkg.description}</p>
                  <ul className="space-y-2 mt-auto">
                    {pkg.includes.map((inc) => (
                      <li key={inc} className="flex items-center gap-2 text-base text-foreground/85">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                        {inc}
                      </li>
                    ))}
                  </ul>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="container mx-auto px-4 text-center">
          <ScrollReveal>
            <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-6">
              Bereit für dein <span className="text-gradient">{section.title}</span>-Projekt?
            </h2>
            <p className="text-foreground/85 max-w-xl mx-auto mb-10">Erzähl uns von deinem Vorhaben & wir melden uns mit einem passenden Vorschlag.</p>
            <Button asChild size="lg" className="bg-primary hover:bg-primary/80 text-primary-foreground rounded-full px-10 h-14">
              <Link to="/kontakt">Projekt anfragen <ArrowRight className="ml-2" size={20} /></Link>
            </Button>
          </ScrollReveal>
        </div>
      </section>


      <Footer />
    </div>
  );
};

export default LeistungDetail;
